import { ReactNode, useEffect, useRef } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { DashboardIcon, UsersIcon, ServiceIcon, SettingsIcon, LogoutIcon, ChartIcon, CloudIcon, KeyIcon, DownloadIcon } from './ui/Icons'
import AriaChatPanel from './AriaChatPanel/AriaChatPanel'
import ScoutChatPanel from './ScoutChatPanel'
import WelcomeModal from './ui/WelcomeModal'
import { TenantSwitcher } from './TenantSwitcher'
import { useCockpitMode, type CockpitMode } from '../hooks/useCockpitMode'
import { getRuntimeConfig, isTenantInstance } from '../lib/runtimeConfig'
import './Layout.css'

interface Organization {
  id: number
  public_token?: string  // UUID — fuer URL-Routing (Datenschutz)
  name: string
  slug: string
  member_role: string
  caller_onboarding_status?: 'pending' | 'completed' | 'skipped'  // TQ #5710: Mode-Switch Badge
}

interface LayoutProps {
  children: ReactNode
  organization: Organization
  onLogout: () => void
  /** TQ #5387: true wenn Cockpit unter Custom-Domain (cockpit.<tenant-domain>) laeuft.
      Routes haben dann KEIN /:slug-Prefix — NavLinks gehen direkt auf /services usw. */
  customDomain?: boolean
  /** TQ #5710: Wenn Member auf "Fertig" klickt, Parent (App.tsx) updaten. */
  onOnboardingCompleted?: () => void
  /** TQ #6906 Bundle A1: optionaler expliziter Mode-Override (Default: Auto-Detect aus Hostname).
      Genutzt für Tests + zukünftig wenn Personal-Modus on with.it-sce.de aktiviert wird. */
  modeOverride?: CockpitMode
  /** TQ #6906 Bundle A1: Staff-Detection für Mode-Auto-Detect.
      True wenn User cortex-admin / mcp-admin / itsce-staff Role hat. */
  isStaff?: boolean
  /** TQ #6906 Patric-Klarstellung 2026-06-02: Liste der gebuchten Module
      aus cortex.tenant_rate_configs.allowed_modules (z.B. ['aria','mail',
      'caldav','device']). Steuert Tab-Sichtbarkeit für aria/infra. Leeres
      Array = im Personal-Mode alle modul-gegateten Tabs aus, im Org-Mode
      Fallback auf heutiges immer-an-Verhalten. */
  subscribedModules?: string[]
}

/**
 * Tab-Visibility-Regeln pro CockpitMode (TQ #6906 Bundle A1).
 *
 * Die Sidebar zeigt nur Tabs an, die im aktuellen Mode (+ Role + Modules)
 * sichtbar sind. Vorbereitung für Phase A2 (with.it-sce.de Routing-Switch)
 * — heute ist die Sidebar in der Praxis nur im 'org' und 'staff' Modus aktiv,
 * 'personal' wird beim Bundle A2 cutover live.
 */
interface TabVisibility {
  dashboard: boolean
  aria: boolean
  marketplace: boolean
  members: boolean
  services: boolean
  licenses: boolean
  tickets: boolean
  auftraege: boolean
  billing: boolean
  /** TQ #337: Dokumente-Bereich (Angebote/Rechnungen/Verträge/Regieberichte/
      Token-Historie mit Tab-Leiste) — ersetzt den alten separaten "Angebote"-
      Nav-Eintrag. Org-/Staff-only, analog dem bisherigen quotations-Flag. */
  documents: boolean
  /** TQ #341 (25.09.2026, Patric): "Projekte"-Menuepunkt — rein lesende Sicht auf
      ERPNext-Projekte des Kunden (Status/Fortschritt/Aufgaben). Org-/Staff-only,
      analog documents. Personal-Mode hat keinen ERPNext-Organisationskunden. */
  projects: boolean
  infra: boolean
  downloads: boolean
  settings: boolean
  /** IT-SCE Intern (Kunden, Geschaeft, Plattform-Health, Software-Lizenzen, Verdienst-Konten). */
  staffSection: boolean
  /** Personal-spezifische Tabs (MyResources, MyLicenses, MyBilling) — Phase A3/A4 stub. */
  myResources: boolean
  /** Hilfe/Support (Chatwoot-Embed + Direkt-Kontakt). Im Personal-Mode niedrigschwellig sichtbar. */
  support: boolean
}

// Patric 23.09.2026: KI-Aufträge workspace-weit ausblenden, bis die zugrundeliegende
// TQ-Anbindung wieder sauber läuft. Ein Schalter statt drei Stellen (personal/staff/org),
// damit das Wiedereinschalten nicht wieder alle drei Modi einzeln treffen muss.
const AUFTRAEGE_HIDDEN = true

function visibilityFor(
  mode: CockpitMode,
  role: 'owner' | 'admin' | 'member' | string,
  subscribedModules: string[] = [],
): TabVisibility {
  const isAdminOrOwner = role === 'owner' || role === 'admin'
  // TQ #6906 Patric-Klarstellung 2026-06-02: Aria-Tab nur sichtbar wenn der
  // User auch ein Aria-Modul gebucht hat. Modul-Liste kommt aus Cortex
  // tenant_rate_configs.allowed_modules (oder zukünftig per-user).
  const hasAria = subscribedModules.includes('aria')
  if (mode === 'personal') {
    return {
      dashboard: true,
      // TQ #7119 (Patric 2026-06-15): Aria-Tab im Personal-Mode IMMER sichtbar.
      // Entitlement-getriebene Tab-Sichtbarkeit war fragil (per-user vs tenant
      // allowed_modules, /me/token-budget 404). Stattdessen: Tab immer zeigen;
      // die Aria-Seite zeigt 'Aria buchen' wenn nicht gebucht, sonst Chat.
      aria: true,
      marketplace: true,
      members: false,
      services: false, // im personal-Modus: stattdessen Marketplace + MyResources
      licenses: true,  // "Meine Lizenzen"
      tickets: false,
      auftraege: false,
      billing: true,   // "Meine Billing"
      documents: false,
      projects: false,
      // TQ #6962-T4 (Patric 2026-06-19): Infrastruktur/"Meine Geräte"-Tab im
      // Personal-Mode IMMER sichtbar — analog Aria-Tab (#7119). Die Entitlement-
      // getriebene Sichtbarkeit (hasDevice aus /org/token-budget) war fragil
      // (404 → leere allowed_modules → Tab verschwindet). Stattdessen: Tab
      // immer zeigen; InfraPage/Devices zeigt den Empty-State wenn noch kein
      // Gerät enrolled ist + den Hub-Download-Hinweis.
      infra: true,
      downloads: true,
      settings: true,
      staffSection: false,
      myResources: true,
      support: true,
    }
  }
  if (mode === 'staff') {
    return {
      dashboard: true,
      aria: true,  // Staff sieht immer (Org-Aria fuer IT-SCE selbst)
      marketplace: true,
      members: true,
      services: true,
      licenses: false, // Lizenzen unter Services (Spec Informationsarchitektur)
      tickets: true,
      auftraege: !AUFTRAEGE_HIDDEN,
      billing: true,
      documents: true,
      projects: true,
      infra: isAdminOrOwner,
      // Task-2026-00451 Teil 2 (07.10.2026, Marco): Downloads nur noch fuer
      // Owner/Admin. War zuvor fuer jedes Mitglied sichtbar (V1-Entscheidung
      // Patric 2026-05-20, "alles sichtbar, kein Subscription-Filter") —
      // das galt dem Produkt-Sichtbarkeits-Filter, nicht der Rolle. Analog
      // infra/settings gegated.
      downloads: isAdminOrOwner,
      settings: isAdminOrOwner,
      staffSection: true,
      myResources: false,
      support: false,
    }
  }
  // mode === 'org' (Default, heutiges Verhalten + Marketplace dazu)
  return {
    dashboard: true,
    aria: hasAria || subscribedModules.length === 0, // Org: wenn nicht-leere Liste vorhanden, gate; bei leerem fallback sichtbar (heutiges Verhalten)
    // Org-Marktplatz ausgeblendet (Vorschlag Patric 03.10.2026: "vielleicht muss der Marktplatz ganz raus"):
    // die alten Workspace-Pläne sind weg, Firewall/MCP passen nicht zum Token-Modell. Kommt zurück als
    // Services + Token-Preise (Zielbild #474). Route /marketplace bleibt per Direktlink erreichbar.
    marketplace: false,
    members: true,
    services: true,
    licenses: false, // Lizenzen unter Services (Spec Informationsarchitektur)
    tickets: true,
    auftraege: !AUFTRAEGE_HIDDEN,
    billing: true,
    documents: true,
    projects: true,
    infra: isAdminOrOwner,
    downloads: isAdminOrOwner,
    settings: isAdminOrOwner,
    staffSection: false,
    myResources: false,
    support: false,
  }
}

export default function Layout({ children, organization, onLogout, customDomain = false, onOnboardingCompleted, modeOverride, isStaff = false, subscribedModules = [] }: LayoutProps) {
  const runtimeConfig = getRuntimeConfig()
  const autoMode = useCockpitMode(isStaff)
  const mode: CockpitMode = modeOverride ?? autoMode
  const role = organization.member_role
  const vis = visibilityFor(mode, role, subscribedModules)
  const isOnboarding = organization.caller_onboarding_status === 'pending'
  // Slug-Modus: /<token>/services etc. Custom-Domain: direkt /services etc.
  const base = customDomain ? '' : `/${organization.public_token || organization.slug}`

  const mainRef = useRef<HTMLElement>(null)
  const { pathname } = useLocation()

  // Beim Route-Wechsel den Main-Scroll zurueck auf 0 — verhindert, dass z.B.
  // Aria mit der Scroll-Position des vorigen Tabs oeffnet und der Header
  // ausserhalb der Ansicht steht.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [pathname])

  // Auf der Aria-Seite selbst ist der Floating-Toggle redundant und ueberlappt
  // den Senden-Button — Slide-in nur ausserhalb der Aria-Page anbieten.
  // TQ #6651: Seit TQ #6298 ist die kanonische Route /aria (CortexPage mit
  // Tabs); /aria/activity + /cortex sind nur noch Legacy-Redirects. Der alte
  // endsWith('/aria/activity')-Check matchte /aria NICHT mehr -> FAB tauchte
  // wieder auf und ueberlappte den Senden-Button (Regression). Jetzt /aria
  // (+ Sub-Pfade) und /cortex (Legacy) abdecken.
  const isAriaPage =
    pathname.endsWith('/aria') ||
    pathname.includes('/aria/') ||
    pathname.endsWith('/cortex')

  // TQ #6199: Aria-Sidebar-Section ist aufgeklappt wenn der User in der
  // Aria-Sektion ist (entweder Legacy /cortex oder neue /aria/*-Routes).
  const isInAriaSection =
    pathname.endsWith('/cortex') || pathname.includes('/aria')

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'owner': return 'Inhaber'
      case 'admin': return 'Administrator'
      case 'member': return 'Mitglied'
      default: return role
    }
  }

  return (
    <div className="layout">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          {runtimeConfig.logoUrl
            ? <img className="logo-img" src={runtimeConfig.logoUrl} alt={runtimeConfig.customerLabel} />
            : <h1 className="logo">{runtimeConfig.customerLabel}</h1>}
          <p className="portal-name">
            Cockpit
            <span className={`mode-badge mode-badge--${isOnboarding ? 'onboarding' : 'aria'}`}>
              {isOnboarding ? 'Onboarding' : 'Aria'}
            </span>
          </p>
        </div>

        <div className="org-info">
          <div className="org-avatar">
            {organization.name.charAt(0).toUpperCase()}
          </div>
          <div className="org-details">
            <span className="org-name">{organization.name}</span>
            {/* TQ #8000: Org-Rollen-Label nur mit Org-Kontext zeigen. Im
                Personal-Modus (with.it-sce.de) gibt es keine Org -> "Inhaber"
                ist dort sinnlos/verwirrend. */}
            {mode !== 'personal' && (
              <span className="org-role">{getRoleLabel(organization.member_role)}</span>
            )}
          </div>
          {/* TQ #7975: Tenant-Switcher ersetzt den statischen Portal-Zurück-Link. */}
          {!isTenantInstance() && <TenantSwitcher currentSlug={organization.slug} />}
        </div>

        <nav className="sidebar-nav">
          {vis.dashboard && (
            <NavLink to={base || '/'} end className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
              <span className="nav-icon"><DashboardIcon size={20} /></span>
              Dashboard
            </NavLink>
          )}

          {/* TQ #6298: Aria = einzelner NavLink. Sub-Items (Aktivitaet/Persona/
              Org-Defaults/GroupFolders/Audit-Log) wurden in Tab-Bar IN der
              Aria-Page verschoben (CortexPage). */}
          {vis.aria && (
            <NavLink
              to={`${base}/aria`}
              className={() => `nav-item ${isInAriaSection ? 'active' : ''}`}
            >
              <span className="nav-icon"><ChartIcon size={20} /></span>
              Aria
            </NavLink>
          )}

          {/* TQ #6906 Bundle A3: Marketplace-Tab (Service-Katalog). Auch im
              org-Modus sichtbar als Cross-Sell. Route + Page kommen Phase A3. */}
          {vis.marketplace && (
            <NavLink to={`${base}/marketplace`} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
              <span className="nav-icon"><ServiceIcon size={20} /></span>
              Marktplatz
            </NavLink>
          )}

          {/* TQ #6906 Bundle A1: Personal-Modus "Meine Services" (Route /my-resources,
              gleiche Services-Page wie Org-Mode). Label-Angleichung an Org "Services"
              + vermeidet Kollision mit Infra "IaaS-Ressourcen" (Patric 2026-06-03). */}
          {vis.myResources && (
            <NavLink to="/my-resources" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
              <span className="nav-icon"><ServiceIcon size={20} /></span>
              Meine Services
            </NavLink>
          )}

          {vis.members && (
            <NavLink to={`${base}/members`} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
              <span className="nav-icon"><UsersIcon size={20} /></span>
              Mitglieder
            </NavLink>
          )}

          {vis.services && (
            <NavLink to={`${base}/services`} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
              <span className="nav-icon"><ServiceIcon size={20} /></span>
              Services
            </NavLink>
          )}

          {vis.licenses && (
            <NavLink to={`${base}/licenses`} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
              <span className="nav-icon"><KeyIcon size={20} /></span>
              Lizenzen
            </NavLink>
          )}

          {vis.tickets && (
            <NavLink to={`${base}/tickets`} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
              <span className="nav-icon" style={{ fontSize: '1rem' }}>🎫</span>
              Support
            </NavLink>
          )}

          {vis.auftraege && (
            <NavLink to={`${base}/auftraege`} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
              <span className="nav-icon" style={{ fontSize: '1rem' }}>🤖</span>
              KI-Aufträge
            </NavLink>
          )}

          {vis.billing && (
            <NavLink to={`${base}/billing`} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
              <span className="nav-icon" style={{ fontSize: '1rem' }}>💳</span>
              Abrechnung
            </NavLink>
          )}

          {vis.documents && (
            <NavLink to={`${base}/dokumente`} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
              <span className="nav-icon" style={{ fontSize: '1rem' }}>📑</span>
              Dokumente
            </NavLink>
          )}

          {vis.projects && (
            <NavLink to={`${base}/projekte`} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
              <span className="nav-icon" style={{ fontSize: '1rem' }}>📁</span>
              Projekte
            </NavLink>
          )}

          {vis.infra && (
            <NavLink to={`${base}/infra`} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
              <span className="nav-icon"><CloudIcon size={20} /></span>
              Infrastruktur
            </NavLink>
          )}

          {/* TQ #5336: Downloads-Tab fuer alle Mitglieder (memberOnly Auth im Backend). */}
          {vis.downloads && (
            <NavLink to={`${base}/downloads`} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
              <span className="nav-icon"><DownloadIcon size={20} /></span>
              Downloads
            </NavLink>
          )}

          {vis.settings && (
            <NavLink to={`${base}/settings`} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
              <span className="nav-icon"><SettingsIcon size={20} /></span>
              Einstellungen
            </NavLink>
          )}

          {/* TQ #6906 Patric 2026-06-02: Support-Tab im Personal-Mode.
              Im Org-Mode bleibt Tickets der primaere Support-Kanal,
              Personal-User bekommen niedrigschwelligen Direkt-Kontakt. */}
          {vis.support && (
            <NavLink to={`${base}/support`} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
              <span className="nav-icon" aria-hidden style={{ fontSize: '1.1rem' }}>💬</span>
              Hilfe &amp; Support
            </NavLink>
          )}

          {/* TQ #6906 Bundle A1: IT-SCE Intern Sektion — nur staff-Modus.
              Tabs verlinken aktuell auf bestehende Staff-Routes; im
              Unified-Cockpit landen sie spaeter im selben Cockpit-Codebase. */}
          {vis.staffSection && (
            <div className="sidebar-staff-section" style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', opacity: 0.6, padding: '0 1rem 0.5rem' }}>
                IT-SCE Intern
              </div>
              <NavLink to="/staff/customers" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <span className="nav-icon" style={{ fontSize: '1rem' }}>👥</span>
                Kunden
              </NavLink>
              <NavLink to="/staff/invoices" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <span className="nav-icon" style={{ fontSize: '1rem' }}>💼</span>
                Geschäft
              </NavLink>
              <NavLink to="/staff/health" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <span className="nav-icon" style={{ fontSize: '1rem' }}>🛠️</span>
                Plattform-Health
              </NavLink>
              <NavLink to="/staff/licenses" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <span className="nav-icon" style={{ fontSize: '1rem' }}>💾</span>
                Software-Lizenzen
              </NavLink>
              <NavLink to="/staff/earnings" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <span className="nav-icon" style={{ fontSize: '1rem' }}>💰</span>
                Verdienst-Konten
              </NavLink>
              {/* TQ #7128: Aria-Wissensbasis (erste reale /staff/*-Page, read-only) */}
              <NavLink to="/staff/knowledge" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <span className="nav-icon" style={{ fontSize: '1rem' }}>📚</span>
                Wissensbasis
              </NavLink>
              {/* TQ #9696 (Staff-Migration Teil C): Geschäft-Ops-Hub — Service-Katalog,
                  Anfragen, Provisioning-Queue, Tenant onboarden (Einstieg = Service-Katalog) */}
              <NavLink to="/staff/service-catalog" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <span className="nav-icon" style={{ fontSize: '1rem' }}>🧩</span>
                Geschäft-Ops
              </NavLink>
              {/* TQ #9698: Cortex-Verwaltung (Uebersicht + Standing-Permissions) */}
              <NavLink to="/staff/cortex" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <span className="nav-icon" style={{ fontSize: '1rem' }}>🧠</span>
                Cortex-Verwaltung
              </NavLink>
            </div>
          )}
        </nav>

        <div className="sidebar-footer">
          <button className="btn-logout" onClick={() => onLogout()}>
            <span className="nav-icon"><LogoutIcon size={20} /></span>
            Abmelden
          </button>
        </div>
      </aside>

      {/* TQ #7072 Phase 1: Welcome-Modal. Holt beim Mount GET /api/me/welcome-pending
          und zeigt pro noch nicht bestätigtem Service eine Onboarding-Karte. Nach
          dem Schliessen (Ack) erscheint es nicht wieder. Eigene Fehler-/Empty-Logik
          intern — rendert nichts wenn keine pending Welcomes vorliegen. */}
      <WelcomeModal base={base} />

      {/* Main Content */}
      <main className="main-content" ref={mainRef}>
        {children}
      </main>

      {/* TQ #6906 Patric 2026-06-03: FAB-Switch.
          Hat User Aria gebucht → Aria-FAB (heutiges Verhalten).
          Hat User KEIN Aria (Personal-Mode mit leerer Aria-Booking) →
          Scout-FAB als oeffentlicher Berater-Chat (niedrigschwelliger
          Pre-Sales-Kanal, kein Auth-Header).
          Org/Staff-Mode ohne Aria → kein FAB (Org-Aria Default an, falls
          unklar, lassen wir die heutige Aria-Panel-Logik gewinnen). */}
      {!isAriaPage && (() => {
        const userHasAria = subscribedModules.map(m => m.toLowerCase()).includes('aria')
        if (userHasAria) {
          return (
            <AriaChatPanel
              slugOrToken={organization.public_token || organization.slug}
              onboardingPending={isOnboarding}
              onOnboardingCompleted={onOnboardingCompleted}
            />
          )
        }
        // TQ #8277/#8278: Personal-Mode ohne Aria → Scout mit proaktivem
        // Onboarding-Auto-Open (Default heutiges Verhalten bleibt erhalten,
        // Auto-Open nur bei stage 'new_no_booking' & nicht acked & 1x/Session).
        if (mode === 'personal') return <ScoutChatPanel autoOpenHint="onboarding" />
        // Org/Staff fallen auf Aria-Panel zurueck (Org-Aria ist Default an).
        return (
          <AriaChatPanel
            slugOrToken={organization.public_token || organization.slug}
            onboardingPending={isOnboarding}
            onOnboardingCompleted={onOnboardingCompleted}
          />
        )
      })()}
    </div>
  )
}
